/**
 * Order HTTP layer — every order read and write the dashboard performs goes
 * through this file. All of these endpoints hang off the same `orders` table,
 * which is why /orders and /deliveryOrders are two lenses on ONE dataset rather
 * than two separate data sources: they call the same `getAllOrders` with
 * different default filters.
 *
 * ENDPOINT MAP (paths are relative to the API base configured in `api`):
 * | export             | method + path              | purpose                             |
 * |--------------------|----------------------------|-------------------------------------|
 * | createDispatchOrder| POST   api/order/dispatch  | EVERY manual order this app files   |
 * | getAllOrders     | GET    api/order                     | the shared order feed        |
 * | getOrder         | GET    api/order/:id                 | one order, full detail       |
 * | getVendorOrder   | GET    api/vendor-orders/:id         | one order, vendor-scoped     |
 * | updateOrder      | PATCH  api/order/:id                 | status/driver/time/money     |
 * | softDeleteOrder  | PATCH  api/order/:id/soft-delete     | hide from ONE audience       |
 * | removeOrder      | DELETE api/order/:id                 | HARD delete                  |
 * | removeOrders     | DELETE api/delete-orders             | bulk soft delete             |
 * | notifyVendor     | POST   api/order/:id/notify-vendor   | re-ping the vendor           |
 *
 * ONE CALL FOR THREE MODALS. `createOrder` and `createWhatsOrder` used to post
 * to two different URLs; both are gone. The API now derives the order policy
 * from the caller — users.role plus vendors.is_service_vendor — so all three
 * modals make the identical request and the server decides what kind of row it
 * becomes. There is deliberately NO discriminator to send: a field the client
 * could get wrong is a field that could silently change what a vendor is billed.
 *
 * NAMING TRAPS — read before reusing any of these at a new call site:
 * - `createDispatchOrder` is NOT the customer checkout call. Customer checkout
 *   is POST api/order, which this dashboard never calls.
 * - `removeOrder` (singular) destroys the row; `removeOrders` (plural) is a
 *   bulk SOFT delete for one audience. The names do not signal the difference.
 *
 * Known constraint: `edit` is imported from api/apiURLs but that module exports
 * no such binding, so the import resolves to undefined. It is unused here;
 * left in place deliberately.
 */
import { api } from "api";
import { getOrders, getOrderDetails, deleteOrder, edit } from "api/apiURLs";

/**
 * Files a manual order. Used by all three "طلب سريع" modals.
 *
 * WHAT KIND OF ORDER THIS BECOMES IS NOT UP TO THE CALLER. The API resolves it
 * from the logged-in user:
 *
 *   staff admin                        → transcribed sale, type "whatsapp",
 *                                        the operator's fee taken verbatim
 *   vendor with isServiceVendor        → "go fetch this", subtotal 0, born
 *                                        finished, area substituted if absent
 *   any other vendor                   → driver request, born preparing, fee
 *                                        from the area unless overridden
 *
 * So send only the fields the open modal collects and let the server classify.
 * Do NOT add a `source`/`type` field hoping to steer it — the API ignores one in
 * the body by design.
 *
 * @param {FormData} data - The union of what the three modals collect:
 *   notes, areaId, name, phone, address, total, time, shipping, vendorId.
 *   Required set depends on the resolved policy; `vendorId` is the vendor's
 *   USER id (what the vendors dropdown returns), not the vendors-table PK.
 */
export const createDispatchOrder = async (data) => {
    const response = await api.post(`api/order/dispatch`, data);
    return response.data;
};

/**
 * The form of a manual order for the signed-in account (GET
 * api/order/dispatch/form): whether the order counts in the statement, and the
 * sentence under the price. The API writes the sentence, so the tablet and
 * this dashboard show the same one, from the same rule.
 *
 * @returns {Promise<{countsInStatement: boolean, hints: {total?: string}}>}
 */
export const getDispatchOrderForm = async () => {
    const response = await api.get("api/order/dispatch/form");
    return response.data;
};

/**
 * Fetches the shared order feed used by BOTH order screens.
 *
 * No `type` filter is ever sent, so normal, delivery and whatsapp orders come
 * back intermixed; the two screens differ only in their default
 * `deletedForVendor` / `deletedForDelivery` filter, their columns and their
 * actions.
 *
 * @param {object} params - page, size, and any of vendorId, deliveryId, status,
 *   startDate, endDate, search, cityId, deletedForVendor, deletedForDelivery.
 * @returns {Promise<object>} `{ results, pages, count }`. Dashboard aggregates
 *   are intentionally loaded through getOrderStats so this request is never
 *   held up by full-filter SUM work.
 */
export const getAllOrders = async (params) => {
    const response = await api.get(getOrders, { params });
    return response.data;
};

/**
 * Fetches full-filter order aggregates independently from the paginated rows.
 * This lets the table render even when a wide date range makes SUM operations
 * slow. `includeBillable` is enabled only by the vendor-settlement screen; the
 * delivery screen avoids that additional aggregate scan.
 *
 * @param {object} params - Same filters as getAllOrders; page and size are ignored.
 * @param {boolean} includeBillable - Whether vendor-statement figures are required.
 * @returns {Promise<object>} count, total, totalShipping and optional billed fields.
 */
export const getOrderStats = async (params, includeBillable = false) => {
    const response = await api.get(`${getOrders}/stats`, {
        params: { ...params, page: undefined, size: undefined, includeBillable },
    });
    return response.data;
};

/**
 * Fetches one order with its full detail payload (cart lines, associations).
 *
 * Used by both detail screens, by the row "نسخ بيانات الطلب" action, and by the
 * auto-print queue in App.js for non-vendor dashboards.
 *
 * @param {number|string} id - Order id.
 * @returns {Promise<object>} `{ order }` — callers must unwrap `.order`.
 */
export const getOrder = async (id) => {
    const response = await api.get(`${getOrderDetails}${id}`);
    return response.data;
};

/**
 * Vendor-scoped single-order fetch. Only used by the auto-print queue in
 * App.js when the dashboard is running as a vendor (`role === "vendor"`),
 * because that session must not read another vendor's order.
 *
 * @param {number|string} id - Order id.
 * @param {number|string} vendorId - The vendor's USER id (App.js passes
 *   `adminInfo.id`), sent as a query param rather than derived from the token.
 */
export const getVendorOrder = async (id, vendorId) => {
    const response = await api.get(`api/vendor-orders/${id}`, {
        params: { vendorId },
    });
    return response.data;
};

/**
 * The single write behind every inline row edit: status dropdown, prep-time
 * stepper, driver assignment and driver reset all funnel through here.
 *
 * A status change fans out server-side (customer WhatsApp/FCM, driver alarms,
 * timeline row), so this is never a purely local edit. There is no transition
 * validation, so any status can follow any other in either direction.
 *
 * @param {number|string} id - Order id.
 * @param {FormData|object} data - Partial order fields. Send a plain object
 *   when a field must be cleared: FormData stringifies values, so
 *   `deliveryId: null` only survives as JSON.
 */
export const updateOrder = async (id, data) => {
    const response = await api.patch(`api/order/${id}`, data);
    return response.data;
};
/**
 * Hides ONE order from ONE audience by setting the matching `deleted_for_*`
 * flag. The row survives and the other audience still sees it — this is the
 * per-row half of the billing workflow (settle, then hide the settled orders).
 *
 * @param {number|string} id - Order id.
 * @param {"vendor"|"delivery"} deleteFor - Which audience loses visibility.
 */
export const softDeleteOrder = async (id, deleteFor) => {
    const response = await api.patch(`api/order/${id}/soft-delete`, { deleteFor });
    return response.data;
};
/**
 * HARD-deletes the order row. Not reversible and not audience-scoped — use
 * `softDeleteOrder` for the billing workflow. Offered from the row menu on both
 * order screens.
 *
 * @param {number|string} id - Order id.
 */
export const removeOrder = async (id) => {
    const response = await api.delete(`${deleteOrder}${id}`);
    return response.data;
};
/**
 * Bulk SOFT delete: hides every order matching `params` from one audience.
 *
 * Callers must pass the screen's CURRENT list filters — every narrowing filter
 * is optional server-side, so an under-specified call matches far more than the
 * operator sees on screen. Both screens therefore only expose the button once a
 * vendor (or driver) filter is active, and spread their live params into it.
 *
 * @param {object} params - The list filters plus `deleteFor: "vendor"|"delivery"`.
 */
export const removeOrders = async (params) => {
    const response = await api.delete(`api/delete-orders`, { params });
    return response.data;
};
/**
 * Re-sends the "new order" notification to the vendor. Exists because vendors
 * miss the in-app alert (phone asleep, app killed, printer offline) and the
 * operator needs a way to nudge them without changing the order.
 *
 * @param {number|string} orderId - Order id.
 */
export const notifyVendor = async (orderId) => {
    const response = await api.post(`api/order/${orderId}/notify-vendor`);
    return response.data;
};
